import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";

/**
 * Client for the audit-log read APIs. Cookie-authenticated.
 *  - `fetchAuditLog`      → `/api/audit`        (HR, scoped to the workspace)
 *  - `fetchAdminAuditLog` → `/api/admin/audit`  (platform admin, all workspaces)
 *
 * The log is read-only here; entries are written server-side from the action
 * handlers, never through these endpoints.
 */

export type AuditCategory = "data" | "auth" | "access" | "admin";

/** Before/after values for the fields an action changed. */
export type AuditChanges = Record<string, { from: unknown; to: unknown }>;

export type AuditEntry = {
  id: string;
  actorType: string;
  actorId: string | null;
  actorName: string | null;
  actorRole: string | null;
  action: string;
  category: AuditCategory;
  status: string;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  subjectEmployeeId: string | null;
  changes: AuditChanges | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  ownerType: string;
  ownerId: string;
};

export type AuditPage = { items: AuditEntry[]; total: number };

export type AuditQuery = {
  q?: string;
  category?: string;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
  /** Platform view only: include soft-deleted (archived) entries. */
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
  sort?: AuditSortKey;
  dir?: "asc" | "desc";
};

/** The columns the audit table sorts by. */
export type AuditSortKey = "when" | "action" | "actor" | "record";

/** Audit-log retention policy (platform-admin configurable). */
export type AuditRetentionMode = "hard" | "soft";
export type AuditRetention = {
  enabled: boolean;
  days: number;
  mode: AuditRetentionMode;
};

class AuditApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function toQueryString(params: AuditQuery): string {
  const sp = new URLSearchParams();
  if (params.q?.trim()) sp.set("q", params.q.trim());
  if (params.category && params.category !== "all") {
    sp.set("category", params.category);
  }
  if (params.action?.trim()) sp.set("action", params.action.trim());
  if (params.actorId?.trim()) sp.set("actorId", params.actorId.trim());
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.includeArchived) sp.set("includeArchived", "true");
  if (params.sort) sp.set("sort", params.sort);
  if (params.dir) sp.set("dir", params.dir);
  sp.set("limit", String(params.limit ?? 50));
  sp.set("offset", String(params.offset ?? 0));
  return `?${sp.toString()}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new AuditApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new AuditApiError(message, res.status);
  }
  return data as T;
}

export function fetchAuditLog(params: AuditQuery): Promise<AuditPage> {
  return request<AuditPage>(`/api/audit${toQueryString(params)}`);
}

export function fetchAdminAuditLog(params: AuditQuery): Promise<AuditPage> {
  return request<AuditPage>(`/api/admin/audit${toQueryString(params)}`);
}

async function downloadCsv(path: string, filename: string): Promise<void> {
  const res = await apiFetch(apiUrl(path), { credentials: "include" });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Couldn't download audit log (${res.status})`;
    throw new AuditApiError(message, res.status);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Downloads the filtered audit log as a CSV. */
export function downloadAuditLog(
  scope: "workspace" | "platform",
  params: AuditQuery,
): Promise<void> {
  const base = scope === "platform" ? "/api/admin/audit" : "/api/audit";
  const prefix = scope === "platform" ? "platform-activity-log" : "activity-log";
  const date = new Date().toISOString().slice(0, 10);
  return downloadCsv(`${base}/export${toQueryString(params)}`, `${prefix}-${date}.csv`);
}

/** Reads the current audit-log retention policy (admin only). */
export function fetchAuditRetention(): Promise<AuditRetention> {
  return request<AuditRetention>("/api/admin/audit/retention");
}

/** Saves the audit-log retention policy (admin only). */
export function updateAuditRetention(config: AuditRetention): Promise<AuditRetention> {
  return request<AuditRetention>("/api/admin/audit/retention", {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

export { AuditApiError };
